import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import LogicalDivider from 'widgets-v6/logical-divider';
import Separator from 'widgets-v5/separator';
/** import widgets components in the section above, Sort Lines Ascending */

import { getWeatherConditionFieldLabels } from 'states/resources/weather-conditions/selectors';
import { WeatherConditionFieldsTableRow } from './weather-condition-history-fields';
/** import single application imports in the section above, Sort Lines Ascending */

/** Fixed equal columns so a long qualifier cannot shift Value or Time Frame. */
const WEATHER_CONDITION_HISTORY_TABLE_STYLE = {
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    width: '100%',
};

const WEATHER_CONDITION_HISTORY_COLUMN_STYLE = { width: '25%' };

/**
 * Value text only. The parent history cell sets wordBreak break-all, so these
 * values wrap like the creative Name cell. Headers keep the cell style.
 */
const WEATHER_CONDITION_HISTORY_VALUE_STYLE = {
    whiteSpace: 'normal',
    wordBreak: 'normal',
    overflowWrap: 'anywhere',
};

/**
 * Standalone Weather Conditions block for ad history (table columns + OR/AND chips between rows).
 *
 * @param {Object} props
 * @param {Object} props.condition - Weather condition from snapshot
 * @param {string} [props.title] - Condition heading (e.g. "Condition 1")
 * @param {boolean} [props.showDivider] - Whether to show OR/AND after this condition
 * @param {string} [props.logicRelationship] - "and" | "or"
 * @param {Object} props.weatherConditionsData - Bound selector fns from Redux
 */
export const WeatherConditionHistorySummary = ({
    condition,
    title,
    showDivider,
    logicRelationship,
    weatherConditionsData,
}) => (
    <React.Fragment>
        {title && (
            <Separator
                label={
                    <Typography variant="subtitle1" gutterBottom>
                        {title}
                    </Typography>
                }
            />
        )}
        <table style={WEATHER_CONDITION_HISTORY_TABLE_STYLE}>
            <colgroup>
                <col style={WEATHER_CONDITION_HISTORY_COLUMN_STYLE} />
                <col style={WEATHER_CONDITION_HISTORY_COLUMN_STYLE} />
                <col style={WEATHER_CONDITION_HISTORY_COLUMN_STYLE} />
                <col style={WEATHER_CONDITION_HISTORY_COLUMN_STYLE} />
            </colgroup>
            <tbody>
                <WeatherConditionFieldsTableRow
                    condition={condition}
                    weatherConditionsData={weatherConditionsData}
                    valueStyle={WEATHER_CONDITION_HISTORY_VALUE_STYLE}
                />
            </tbody>
        </table>
        {showDivider && logicRelationship && (
            <React.Fragment>
                {/* Chip-only OR/AND between conditions; no horizontal rule in history compare */}
                <LogicalDivider label={logicRelationship.toUpperCase()} hideLine />
            </React.Fragment>
        )}
    </React.Fragment>
);

/**
 * Plain-text Weather Conditions for the history Copy button.
 * Each condition is a heading plus Condition, Qualifier, Value, and Time Frame,
 * the same labeled lines Rotation Method uses. OR / AND stays between conditions.
 * No conditions copies as None.
 *
 * @param {Object} weatherConditions - Snapshot `weatherConditions`, with `conditions` and `logicRelationship`.
 * @param {Object} weatherConditionsData - Bound weather label functions.
 * @returns {string}
 */
export function formatWeatherConditionsClipboardText(weatherConditions, weatherConditionsData) {
    const conditions = Array.isArray(weatherConditions?.conditions)
        ? weatherConditions.conditions
        : [];
    if (conditions.length === 0) {
        return 'None';
    }

    const blocks = conditions.map((condition, index) => {
        const labels = getWeatherConditionFieldLabels(condition, weatherConditionsData);
        return [
            `Condition ${index + 1}`,
            `Condition: ${labels.conditionLabel}`,
            `Qualifier: ${labels.qualifierLabel}`,
            `Value: ${labels.valueLabel}`,
            `Time Frame: ${labels.timeframeLabel}`,
        ].join('\n');
    });

    const relationship = weatherConditions.logicRelationship
        ? String(weatherConditions.logicRelationship).toUpperCase()
        : '';
    if (!relationship || blocks.length < 2) {
        return blocks.join('\n\n');
    }

    return blocks.join(`\n\n${relationship}\n\n`);
}
